import {Extension, Node} from '@tiptap/core';
import type {Node as ProseMirrorNode, NodeType} from '@tiptap/pm/model';
import {TextSelection} from '@tiptap/pm/state';
import type {EditorState, Transaction} from '@tiptap/pm/state';

/**
 * Lists as CKEditor 4 wrote them: `<ul><li>text</li></ul>`, the text right in the item (TipTap's
 * own list item holds a paragraph, which writes `<li><p>`), one level deep. The commands work on
 * the top-level blocks the selection touches: making a list turns each paragraph or heading into an
 * item (and joins lists of the other kind into it), and undoing it turns each item back into a
 * paragraph. Enter in an empty item, and Backspace at the start of the first, leave the list.
 */

export const ListItem = Node.create({
  name: 'listItem',
  content: 'inline*',
  defining: true,
  parseHTML: () => [{tag: 'li'}],
  renderHTML: ({HTMLAttributes}) => ['li', HTMLAttributes, 0],
});

export const BulletList = Node.create({
  name: 'bulletList',
  group: 'block',
  content: 'listItem+',
  parseHTML: () => [{tag: 'ul'}],
  renderHTML: ({HTMLAttributes}) => ['ul', HTMLAttributes, 0],
});

export const OrderedList = Node.create({
  name: 'orderedList',
  group: 'block',
  content: 'listItem+',
  parseHTML: () => [{tag: 'ol'}],
  renderHTML: ({HTMLAttributes}) => ['ol', HTMLAttributes, 0],
});

export type ListKind = 'bulletList' | 'orderedList';

const isList = (node: ProseMirrorNode | null | undefined): boolean =>
  !!node && (node.type.name === 'bulletList' || node.type.name === 'orderedList');

/** The top-level blocks the selection touches, as [first index, last index]. */
const touchedBlocks = (state: EditorState): [number, number] => {
  const {$from, $to} = state.selection;
  return [$from.index(0), $to.index(0)];
};

const blockStart = (doc: ProseMirrorNode, index: number): number => {
  let position = 0;
  for (let i = 0; i < index; i++) {
    position += doc.child(i).nodeSize;
  }
  return position;
};

/** Whether every block the selection touches is a list of this kind. */
export const isListActive = (state: EditorState, kind: ListKind): boolean => {
  const [first, last] = touchedBlocks(state);
  for (let i = first; i <= last; i++) {
    if (state.doc.child(i).type.name !== kind) {
      return false;
    }
  }
  return true;
};

export const toggleList = (state: EditorState, dispatch: (tr: Transaction) => void, kind: ListKind): boolean => {
  const {schema, doc} = state;
  const [first, last] = touchedBlocks(state);
  const from = blockStart(doc, first);
  const to = blockStart(doc, last + 1);
  const paragraph = schema.nodes['paragraph'];
  const item = schema.nodes['listItem'];
  const replacement: ProseMirrorNode[] = [];
  if (isListActive(state, kind)) {
    for (let i = first; i <= last; i++) {
      doc.child(i).forEach((listItem) => replacement.push(paragraph.create(null, listItem.content)));
    }
  } else {
    const items: ProseMirrorNode[] = [];
    for (let i = first; i <= last; i++) {
      const block = doc.child(i);
      if (isList(block)) {
        block.forEach((listItem) => items.push(listItem));
      } else if (block.isTextblock) {
        items.push(item.create(null, block.content));
      } else {
        items.push(item.create());
      }
    }
    replacement.push((schema.nodes[kind] as NodeType).create(null, items));
  }
  const tr = state.tr.replaceWith(from, to, replacement);
  const cursor = Math.min(from + 2, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(cursor)));
  dispatch(tr.scrollIntoView());
  return true;
};

/** The item the cursor is in, when the selection is a cursor in a list item. */
const itemAtCursor = (state: EditorState) => {
  const {$from, empty} = state.selection;
  if (!empty || $from.parent.type.name !== 'listItem') {
    return null;
  }
  return {$from, item: $from.parent, list: $from.node(-1), index: $from.index(-1)};
};

/** Enter in an empty item: the list ends there, and a paragraph follows it. */
const leaveFromEmptyItem = (state: EditorState, dispatch: (tr: Transaction) => void): boolean => {
  const found = itemAtCursor(state);
  if (!found || found.item.content.size) {
    return false;
  }
  const {$from, list, index} = found;
  const listStart = $from.before(-1);
  const before: ProseMirrorNode[] = [];
  const after: ProseMirrorNode[] = [];
  list.forEach((child, _offset, i) => {
    if (i < index) {
      before.push(child);
    } else if (i > index) {
      after.push(child);
    }
  });
  const nodes: ProseMirrorNode[] = [];
  if (before.length) {
    nodes.push(list.type.create(list.attrs, before));
  }
  nodes.push(state.schema.nodes['paragraph'].create());
  if (after.length) {
    nodes.push(list.type.create(list.attrs, after));
  }
  const tr = state.tr.replaceWith(listStart, listStart + list.nodeSize, nodes);
  const paragraphStart = listStart + (before.length ? nodes[0].nodeSize : 0) + 1;
  tr.setSelection(TextSelection.create(tr.doc, paragraphStart));
  dispatch(tr.scrollIntoView());
  return true;
};

/** Backspace at the start of the first item: it becomes a paragraph before the list. */
const leaveFromFirstItem = (state: EditorState, dispatch: (tr: Transaction) => void): boolean => {
  const found = itemAtCursor(state);
  if (!found || found.index !== 0 || found.$from.parentOffset !== 0) {
    return false;
  }
  const {$from, item, list} = found;
  const listStart = $from.before(-1);
  const rest: ProseMirrorNode[] = [];
  list.forEach((child, _offset, i) => {
    if (i > 0) {
      rest.push(child);
    }
  });
  const nodes: ProseMirrorNode[] = [state.schema.nodes['paragraph'].create(null, item.content)];
  if (rest.length) {
    nodes.push(list.type.create(list.attrs, rest));
  }
  const tr = state.tr.replaceWith(listStart, listStart + list.nodeSize, nodes);
  tr.setSelection(TextSelection.create(tr.doc, listStart + 1));
  dispatch(tr.scrollIntoView());
  return true;
};

export const ListKeys = Extension.create({
  name: 'snoballListKeys',
  priority: 1100,
  addKeyboardShortcuts() {
    return {
      'Enter': () => leaveFromEmptyItem(this.editor.state, this.editor.view.dispatch),
      'Backspace': () => leaveFromFirstItem(this.editor.state, this.editor.view.dispatch),
    };
  },
});
